// Digital_Marketing.tsx
import React from 'react';
import { motion, Variants, easeOut } from 'framer-motion';
import { Link } from 'react-router-dom';

// --- Define Animation Variants ---
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const slideInLeft = {
  hidden: { opacity: 0, x: -50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7 } },
};

const slideInRight = {
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7 } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3,
    },
  },
};

const cardVariant: Variants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.4 } },
  hover: {
    scale: 1.05,
    transition: {
      type: "spring" as const,  // ✅ TypeScript fix
      stiffness: 300
    }
  }
};

const imageReveal = {
  hidden: { clipPath: 'inset(0 100% 0 0)' },
  visible: { clipPath: 'inset(0 0% 0 0)', transition: { duration: 1.2, ease: 'easeInOut' } },
};

const textFadeInUp: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.42, 0, 1, 1], // ✅ equivalent to easeOut
    },
  },
};

const scaleIn: Variants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.8,
      ease: [0.42, 0, 1, 1], // ✅ equivalent to "easeOut"
    },
  },
};
const iconBounce: Variants = {
  initial: { y: 0 },
  hover: {
    y: -10,
    transition: {
      type: "spring" as const,
      stiffness: 300,
      damping: 10
    }
  }
};


const DigitalMarketing: React.FC = () => {
  return (
    <div className="digital-marketing-page min-h-screen bg-gray-50 text-gray-800">

      {/* 1. Hero Section */}
      <motion.section
        className="relative h-[450px] flex items-center justify-center bg-black text-center text-white overflow-hidden"
        initial="hidden"
        animate="visible"
        variants={fadeIn}
      >
        <div className="absolute inset-0 z-0">
          {/* Background Image/Pattern with overlay */}
          <img
            src="https://sixsoftmedia.com/mainssm/wp-content/uploads/2021/01/digital-marketing.jpeg" // Replace with image: data visualization, growth charts, people collaborating with screens
            alt="Digital Marketing Strategy"
            className="opacity-70 w-full h-full object-cover absolute inset-0"
            loading="eager"
          />
          <div className="absolute inset-0 bg-black opacity-40"></div> {/* Dark overlay */}
        </div>
        <div className="z-10 max-w-4xl px-6 -mt-14">
          <motion.h1
            className="text-4xl md:text-5xl font-extrabold leading-tight mb-4 drop-shadow-lg text-orange-400"
            variants={textFadeInUp}
          >
            Hire Expert Digital Marketers
          </motion.h1>
          <motion.p
            className="text-xl md:text-2xl mb-8 opacity-90"
            variants={textFadeInUp}
            transition={{ delay: 0.3 }}
          >
           Starting from just USD 8/Hour - Save 70%
          </motion.p>
          <motion.button
            variants={fadeIn}
            transition={{ delay: 0.6, duration: 0.5 }}
            whileHover={{ scale: 1.05, boxShadow: '0 10px 20px rgba(0,0,0,0.2)' }}
            whileTap={{ scale: 0.95 }}
          >
             <Link
    to="/contact"
    className="inline-block bg-blue-800 hover:bg-blue-500 text-white font-semibold py-3 px-6 rounded-md shadow-lg hover:shadow-xl transition-all duration-300"
  >
    Start Your Digital Journey
  </Link>
          </motion.button>
        </div>
      </motion.section>

      ---

      {/* 2. Why Digital Marketing? Section */}
      <motion.section
        className="py-16 px-8 bg-gray-100"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.4 }}
        variants={staggerContainer}
      >
        <motion.h2
          className="text-4xl font-bold text-center mb-12 text-blue-900"
          variants={fadeIn}
        >
          Why Digital Marketing is Essential Today
        </motion.h2>
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-10">
          {/* Card 1: Reach & Visibility */}
          <motion.div
            className="bg-white p-8 rounded-lg shadow-xl hover:shadow-2xl transition-shadow duration-300 transform hover:-translate-y-2 flex flex-col items-center text-center"
            variants={cardVariant}
            whileHover="hover"
          >
            <motion.div
              className="text-5xl text-blue-500 mb-4"
              variants={iconBounce} // Apply bounce on hover
              whileHover="hover"
            >
              <i className="fas fa-globe"></i> {/* Font Awesome icon */}
            </motion.div>
            <h3 className="text-2xl font-semibold mb-3 text-gray-900">Wider Reach & Global Visibility</h3>
            <p className="text-gray-600">Connect with a massive online audience, transcending geographical boundaries.</p>
          </motion.div>

          {/* Card 2: Measurable Results */}
          <motion.div
            className="bg-white p-8 rounded-lg shadow-xl hover:shadow-2xl transition-shadow duration-300 transform hover:-translate-y-2 flex flex-col items-center text-center"
            variants={cardVariant}
            whileHover="hover"
          >
            <motion.div
              className="text-5xl text-green-500 mb-4"
              variants={iconBounce}
              whileHover="hover"
            >
              <i className="fas fa-chart-line"></i>
            </motion.div>
            <h3 className="text-2xl font-semibold mb-3 text-gray-900">Measurable & Trackable Results</h3>
            <p className="text-gray-600">Gain real-time insights into campaign performance and ROI, allowing for continuous optimization.</p>
          </motion.div>

          {/* Card 3: Cost-Effectiveness */}
          <motion.div
            className="bg-white p-8 rounded-lg shadow-xl hover:shadow-2xl transition-shadow duration-300 transform hover:-translate-y-2 flex flex-col items-center text-center"
            variants={cardVariant}
            whileHover="hover"
          >
            <motion.div
              className="text-5xl text-orange-500 mb-4"
              variants={iconBounce}
              whileHover="hover"
            >
              <i className="fas fa-hand-holding-usd"></i>
            </motion.div>
            <h3 className="text-2xl font-semibold mb-3 text-gray-900">Cost-Effective Strategies</h3>
            <p className="text-gray-600">Achieve higher returns on investment compared to traditional marketing channels.</p>
          </motion.div>

          {/* Card 4: Targeted Audience */}
          <motion.div
            className="bg-white p-8 rounded-lg shadow-xl hover:shadow-2xl transition-shadow duration-300 transform hover:-translate-y-2 flex flex-col items-center text-center"
            variants={cardVariant}
            whileHover="hover"
          >
            <motion.div
              className="text-5xl text-red-500 mb-4"
              variants={iconBounce}
              whileHover="hover"
            >
              <i className="fas fa-bullseye"></i>
            </motion.div>
            <h3 className="text-2xl font-semibold mb-3 text-gray-900">Precise Audience Targeting</h3>
            <p className="text-gray-600">Reach your ideal customers with highly segmented and personalized campaigns.</p>
          </motion.div>

          {/* Card 5: Brand Building */}
          <motion.div
            className="bg-white p-8 rounded-lg shadow-xl hover:shadow-2xl transition-shadow duration-300 transform hover:-translate-y-2 flex flex-col items-center text-center"
            variants={cardVariant}
            whileHover="hover"
          >
            <motion.div
              className="text-5xl text-teal-500 mb-4"
              variants={iconBounce}
              whileHover="hover"
            >
              <i className="fas fa-building"></i>
            </motion.div>
            <h3 className="text-2xl font-semibold mb-3 text-gray-900">Enhanced Brand Building & Trust</h3>
            <p className="text-gray-600">Build credibility, authority, and strong relationships with your audience online.</p>
          </motion.div>

          {/* Card 6: Competitor Advantage */}
          <motion.div
            className="bg-white p-8 rounded-lg shadow-xl hover:shadow-2xl transition-shadow duration-300 transform hover:-translate-y-2 flex flex-col items-center text-center"
            variants={cardVariant}
            whileHover="hover"
          >
            <motion.div
              className="text-5xl text-purple-500 mb-4"
              variants={iconBounce}
              whileHover="hover"
            >
              <i className="fas fa-star"></i>
            </motion.div>
            <h3 className="text-2xl font-semibold mb-3 text-gray-900">Competitive Advantage</h3>
            <p className="text-gray-600">Stay ahead of competitors and capture a larger market share in the digital landscape.</p>
          </motion.div>
        </div>
      </motion.section>

      ---

      {/* 3. Our Digital Marketing Services Section (Categorized) */}
      <motion.section
        className="py-16 px-8 bg-white"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={fadeIn}
      >
        <motion.h2
          className="text-4xl font-bold text-center mb-12 text-blue-900"
          variants={fadeIn}
        >
          Our Holistic Digital Marketing Services
        </motion.h2>
        <div className="max-w-7xl mx-auto">
          {/* Service Category 1: Search Engine Optimization (SEO) */}
          <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.5 }}
              variants={slideInLeft}
              className="flex justify-center md:justify-start pl-60"
            >
              {/* Using standard <img> tag */}
              <img
                src="https://www.transparentpng.com/thumb/seo-png/seo-picture-8.png" // Image: magnifying glass, search bar, graph showing ranking
                alt="Search Engine Optimization (SEO)"
                className="rounded-lg shadow-xl w-[300px] h-[300px]  "
                loading="lazy"
              />
            </motion.div>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.5 }}
              variants={slideInRight}
            >
              <h3 className="text-3xl font-bold mb-4 text-orange-700">Search Engine Optimization (SEO)</h3>
              <p className="text-lg text-gray-700 mb-6">
                Improve your website's visibility on search engines, drive organic traffic, and rank higher for relevant keywords with our expert SEO strategies.
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <motion.li variants={textFadeInUp}>Keyword Research & Analysis</motion.li>
                <motion.li variants={textFadeInUp}>On-Page & Off-Page Optimization</motion.li>
                <motion.li variants={textFadeInUp}>Technical SEO Audits</motion.li>
                <motion.li variants={textFadeInUp}>Local SEO Strategies</motion.li>
              </ul>
              <motion.button
                className="mt-8 "
                whileHover={{ y: -3 }}
                whileTap={{ scale: 0.98 }}
              >
                <Link
    to="/SEO_Expert_In_Noida"
    className="inline-block bg-blue-700 hover:bg-blue-800 text-white font-semibold py-3 px-6 rounded-md shadow-md hover:shadow-lg transition-all duration-300"
  >
    Learn More about SEO
  </Link>
              </motion.button>
            </motion.div>
          </div>

          {/* Service Category 2: Social Media Marketing (SMM) */}
          <div className="grid md:grid-cols-2 gap-12 items-center ml-40">
            <motion.div
              className="md:order-2"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.5 }}
              variants={slideInRight}
            >
              {/* Using standard <img> tag */}
              <img
                src="https://www.pngitem.com/pimgs/m/108-1080144_smm-social-media-marketing-social-networking-no-background.png" // Image: various social media icons, people interacting
                alt="Social Media Marketing"
                className="rounded-lg shadow-xl w-[300px] h-[300px]"
                loading="lazy"
              />
            </motion.div>
            <motion.div
              className="md:order-1"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.5 }}
              variants={slideInLeft}
            >
              <h3 className="text-3xl font-bold mb-4 text-gray-900">Social Media Marketing (SMM)</h3>
              <p className="text-lg text-gray-700 mb-6">
                Build a strong social presence, engage your audience, and drive conversions through tailored social media campaigns across popular platforms.
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <motion.li variants={textFadeInUp}>Social Media Strategy & Management</motion.li>
                <motion.li variants={textFadeInUp}>Content Creation & Curation</motion.li>
                <motion.li variants={textFadeInUp}>Paid Social Advertising</motion.li>
                <motion.li variants={textFadeInUp}>Community Management</motion.li>
              </ul>
  <motion.button
    className="mt-8 "
    whileHover={{ y: -3 }}
    whileTap={{ scale: 0.98 }}
  >
    <Link
      to="/SMM/SMH_Expert_In_Noida"
      className="inline-block bg-blue-700 hover:bg-blue-500 text-white font-semibold py-3 px-6 rounded-md shadow-md hover:shadow-lg transition-all duration-300"
    >
      Learn More about SMM
    </Link>
  </motion.button>
            </motion.div>
          </div>

          {/* Service Category 3: Pay-Per-Click (PPC) Advertising */}
          <div className="grid md:grid-cols-2 gap-12 items-center ml-40">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.5 }}
              variants={slideInLeft}
            >
              {/* Using standard <img> tag */}
              <img
                src="https://media.istockphoto.com/id/1251358514/vector/pay-per-click-concept-tiny-characters-at-huge-computer-desktop-with-cursor-clicking-on-ad.jpg?s=612x612&w=0&k=20&c=H51FfwwAvsVugOAUp2iHBB9aO8f4pdxTBO9_R6GFYtM=" // Image: ad campaigns, graph with upward arrow, dollar signs
                alt="Pay-Per-Click Advertising"
                className="rounded-lg shadow-xl w-[400px] h-[300px]"
                loading="lazy"
              />
            </motion.div>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.5 }}
              variants={slideInRight}
            >
              <h3 className="text-3xl font-bold mb-4 text-gray-900">Pay-Per-Click (PPC) Advertising</h3>
              <p className="text-lg text-gray-700 mb-6">
                Get immediate results with targeted PPC campaigns on Google, Bing, and other platforms, maximizing your ad spend for optimal conversions.
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <motion.li variants={textFadeInUp}>Google Ads Management</motion.li>
                <motion.li variants={textFadeInUp}>Bing Ads Management</motion.li>
                <motion.li variants={textFadeInUp}>Ad Copy & Landing Page Optimization</motion.li>
                <motion.li variants={textFadeInUp}>Remarketing Campaigns</motion.li>
              </ul>
              <motion.button
                className="mt-8 "
                whileHover={{ y: -3 }}
                whileTap={{ scale: 0.98 }}
              >
               <Link
      to="/PPC_Expert_In_Noida"
      className="inline-block bg-blue-700 hover:bg-blue-500 text-white font-semibold py-3 px-6 rounded-md shadow-md hover:shadow-lg transition-all duration-300"
    >
      Learn More about PPC
    </Link>
              </motion.button>
            </motion.div>
          </div>

          {/* Add more service categories as needed: Content Marketing, Email Marketing, Web Design & Development, Analytics & Reporting
          {/* Example: Content Marketing */}
          {/* <div className="grid md:grid-cols-2 gap-12 items-center ml-40">
            <motion.div
              className="md:order-2"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.5 }}
              variants={slideInRight}
            >
              <img
                src="https://www.searchenginejournal.com/wp-content/uploads/2023/01/content-marketing-statistics-63b58be13d3f3-sej.png" // Image: person writing, blog icons, video production
                alt="Content Marketing"
                className="rounded-lg shadow-xl w-[550px] h-[300px]"
                loading="lazy"
              />
            </motion.div>
            <motion.div
              className="md:order-1"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.5 }}
              variants={slideInLeft}
            >
              <h3 className="text-3xl font-bold mb-4 text-gray-900">Content Marketing</h3>
              <p className="text-lg text-gray-700 mb-6">
                Attract, engage, and convert your audience with valuable, relevant, and consistent content designed to establish your brand as an industry leader.
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <motion.li variants={textFadeInUp}>Blog & Article Writing</motion.li>
                <motion.li variants={textFadeInUp}>Video Marketing</motion.li>
                <motion.li variants={textFadeInUp}>Infographics & E-books</motion.li>
                <motion.li variants={textFadeInUp}>Content Strategy Development</motion.li>
              </ul>
              <motion.button
                className="mt-8 "
                whileHover={{ y: -3 }}
                whileTap={{ scale: 0.98 }}
              >
                <Link
      to="/SMM/Video_Marketing_In_Noida"
      className="inline-block bg-blue-700 hover:bg-blue-500 text-white font-semibold py-3 px-6 rounded-md shadow-md hover:shadow-lg transition-all duration-300"
    >
      Learn More about CM
    </Link>
              </motion.button>
            </motion.div>
          </div> */}

        </div>
      </motion.section> 

      ---

      {/* 4. Our Data-Driven Approach Section */}
      <motion.section
        className="py-16 px-8 bg-gradient-to-br from-blue-50 to-purple-100"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={staggerContainer}
      >
        <motion.h2
          className="text-4xl font-bold text-center mb-12 text-blue-800"
          variants={fadeIn}
        >
          Our Data-Driven Approach to Digital Success
        </motion.h2>
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Step 1: Research & Analysis */}
          <motion.div
            className="flex flex-col items-center text-center p-6 bg-white rounded-lg shadow-md"
            variants={scaleIn}
          >
            <motion.div
              className="text-5xl text-blue-500 mb-4"
              whileHover={{ rotate: 15, scale: 1.1 }}
            >
              <i className="fas fa-search"></i>
            </motion.div>
            <h3 className="text-xl font-semibold mb-2 text-gray-900">1. Research & Analysis</h3>
            <p className="text-gray-600">Deep dive into your market, competitors, and audience to uncover opportunities.</p>
          </motion.div>

          {/* Step 2: Strategy Development */}
          <motion.div
            className="flex flex-col items-center text-center p-6 bg-white rounded-lg shadow-md"
            variants={scaleIn}
            transition={{ delay: 0.1 }}
          >
            <motion.div
              className="text-5xl text-green-500 mb-4"
              whileHover={{ rotate: -15, scale: 1.1 }}
            >
              <i className="fas fa-lightbulb"></i>
            </motion.div>
            <h3 className="text-xl font-semibold mb-2 text-gray-900">2. Strategy Development</h3>
            <p className="text-gray-600">Craft a tailored, multi-channel digital marketing strategy for your goals.</p>
          </motion.div>

          {/* Step 3: Implementation & Execution */}
          <motion.div
            className="flex flex-col items-center text-center p-6 bg-white rounded-lg shadow-md"
            variants={scaleIn}
            transition={{ delay: 0.2 }}
          >
            <motion.div
              className="text-5xl text-orange-500 mb-4"
              whileHover={{ y: -5, scale: 1.1 }}
            >
              <i className="fas fa-rocket"></i>
            </motion.div>
            <h3 className="text-xl font-semibold mb-2 text-gray-900">3. Implementation & Execution</h3>
            <p className="text-gray-600">Launch and manage campaigns with precision and expert oversight.</p>
          </motion.div>

          {/* Step 4: Monitoring & Optimization */}
          <motion.div
            className="flex flex-col items-center text-center p-6 bg-white rounded-lg shadow-md"
            variants={scaleIn}
            transition={{ delay: 0.3 }}
          >
            <motion.div
              className="text-5xl text-red-500 mb-4"
              whileHover={{ x: 5, scale: 1.1 }}
            >
              <i className="fas fa-chart-pie"></i>
            </motion.div>
            <h3 className="text-xl font-semibold mb-2 text-gray-900">4. Monitoring & Optimization</h3>
            <p className="text-gray-600">Continuously track performance, analyze data, and refine campaigns for maximum ROI.</p>
          </motion.div>
        </div>
      </motion.section>





    </div>
  );
};

export default DigitalMarketing;